import styled from "@emotion/styled";
import {
  Chip,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MagnifyingGlass, Receipt } from "@phosphor-icons/react";
import { Button, TextField } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import {
  useExportRevenues,
  useRevenueStore,
  useRevenuesPaginated
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { EthiopianDateField } from "../../components/EthiopianDateField";

const Container = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
`;

const PrimaryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const SearchIcon = styled(MagnifyingGlass)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

// const RevenueSettingButton = styled.button`
//   outline: none;
//   border: none;
//   background: none;
//   color: #789;
//   display: flex;
//   align-items: center;
//   gap: 4px;
//   cursor: pointer;
//   font-weight: 500;
// `;

const ChequeReconcileButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const RevenueTable = ({ onEdit }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });
  const [revenueStatus, setRevenueStatus] = useState("all");
  const [voidAttachmentToView, setVoidAttachmentToView] = useState("");

  const openRecordRevenueModal = useRevenueStore(
    state => state.openRecordRevenueModal
  );
  const openUpdateRevenueModal = useRevenueStore(
    state => state.openUpdateRevenueModal
  );
  const navigate = useNavigate();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const revenueQuery = useRevenuesPaginated({
    churchId: selectedChurch?.id,
    page,
    pageSize,
    queries: {
      search: debouncedSearch,
      startDate: filterDates.startDate,
      endDate: filterDates.endDate,
      filter:
        revenueStatus === "all"
          ? {
              is_reconciled: [
                {
                  operator: FILTER_OPERATIONS.IN,
                  value: [null, true]
                }
              ],
              is_voided: [
                {
                  operator: FILTER_OPERATIONS.EQUAL,
                  value: false
                }
              ]
            }
          : {
              is_voided: [
                {
                  operator: FILTER_OPERATIONS.EQUAL,
                  value: true
                }
              ]
            }
    }
  });

  const { mutate: exportRevenues, isLoading: isExporting } =
    useExportRevenues();

  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search]
  );

  const columns = [
    {
      field: "id",
      headerName: "Revenue ID",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography sx={{ fontSize: "14px" }}>
              {row.transaction_number}
            </Typography>
            {Boolean(row.is_voided) && (
              <Chip size="small" color="error" label="Void" />
            )}
          </Stack>
        );
      }
    },
    {
      field: "full_name",
      headerName: "Name",
      flex: 1,
      renderCell: ({ row }) => {
        if (row.full_name) return <span>{row.full_name}</span>;

        if (row.members?.length > 0) {
          return (
            <span>
              {row.members.length === 1
                ? `${row.members[0].first_name} ${row.members[0].last_name}`
                : `${row.members[0].first_name} and ${
                    row.members.length - 1
                  } others`}
            </span>
          );
        }

        if (row.partners?.length > 0) {
          return (
            <span>
              {row.partners.length === 1
                ? `${row.partners[0].first_name} ${row.partners[0].last_name}`
                : `${row.partners[0].first_name} and ${
                    row.partners.length - 1
                  } others`}
            </span>
          );
        }

        return <span>-</span>;
      }
    },
    {
      field: "receipt_number",
      headerName: "Receipt Number",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{row.receipt_number || row.reference_number}</span>;
      }
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      valueFormatter: ({ value }) => {
        return Number(value).toLocaleString();
      }
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{row.chart_of_account?.account_name}</span>;
      }
    },
    {
      field: "method",
      headerName: "Method",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{row.revenue_method?.name}</span>;
      }
    },
    {
      field: "date",
      headerName: "Transaction date",
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{moment(row.received_date).format("DD-MM-YYYY")}</span>;
      }
    },
    {
      field: "void_attachment",
      headerName: "Void Attachment",
      flex: 1,
      hide: revenueStatus !== "void",
      renderCell: ({ row }) => {
        if (row.void_attachments === "[]" || !row.void_attachments) return null;

        return (
          <Button
            flavor="white"
            style={{
              fontSize: "12px"
            }}
            onClick={e => {
              e.stopPropagation();
              setVoidAttachmentToView(JSON.parse(row.void_attachments)[0]);
            }}
          >
            View Attachment
          </Button>
        );
      }
    }
  ];

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  return (
    <Container>
      <Stack alignItems="flex-start" spacing={4}>
        <Header>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Title>Revenue Table</Title>
            <Select
              value={revenueStatus}
              onChange={e => setRevenueStatus(e.target.value)}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="void">Void</MenuItem>
            </Select>
          </Stack>
          <PrimaryActions>
            <ChequeReconcileButton
              flavor="outlined"
              onClick={() =>
                navigate("/finances/temp/revenue/cheque-reconcile")
              }
            >
              <Receipt size={16} />
              <span>Cheque Reconcile</span>
            </ChequeReconcileButton>
            {/* <RevenueSettingButton
              onClick={() => navigate("/finances/temp/revenue/settings")}
            >
              <GearSix size={16} />
              <span>Revenue Setting</span>
            </RevenueSettingButton> */}
            <Button onClick={openRecordRevenueModal}>Record Revenue</Button>
          </PrimaryActions>
        </Header>
        <SecondaryActions>
          <Stack direction="row" alignItems="center" spacing={2}>
            <EthiopianDateField
              size="small"
              label="Start date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
            <EthiopianDateField
              size="small"
              label="End date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
            <Button onClick={handleFilter}>Filter</Button>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <TextField
              neutral={1}
              placeholder="Search revenue"
              register={() => {}}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={16} />
                  </InputAdornment>
                )
              }}
              autoComplete="off"
              onChange={e => setSearch(e.target.value)}
            />
            <Button
              onClick={() =>
                exportRevenues({
                  churchId: selectedChurch?.id,
                  queries: {
                    search: debouncedSearch,
                    startDate: filterDates.startDate,
                    endDate: filterDates.endDate
                  }
                })
              }
              disabled={isExporting}
            >
              Export
            </Button>
          </Stack>
        </SecondaryActions>
      </Stack>
      <DataGrid
        autoHeight
        rows={revenueQuery.data?.result?.data || []}
        rowCount={revenueQuery.data?.result?.total || 0}
        loading={revenueQuery.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        density="comfortable"
        getRowId={row => row.id}
        disableColumnMenu
        disableSelectionOnClick
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
        onRowClick={({ row }) => {
          onEdit(row);
          openUpdateRevenueModal();
        }}
      />
      <Lightbox
        open={!!voidAttachmentToView}
        close={() => setVoidAttachmentToView("")}
        slides={[{ src: `${IMAGE_URL}/${voidAttachmentToView}` }]}
      />
    </Container>
  );
};
